// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// The plan drawing. It owns nothing but composition: one <g> per concept,
// painted back-to-front (shell → rows → airflow → units → axes), each item
// delegated to its own component. Data arrives as a FloorPlanLayout, so the
// swap from seed geometry to digital-twin geometry is a service change, not a
// change here.

import React from 'react';
import type { FloorPlanLayout } from '../../../types/floorPlan';
import PlanRoomShape from './PlanRoomShape';
import PlanRackRow from './PlanRackRow';
import PlanUnitSymbol from './PlanUnitSymbol';
import PlanAirflowRun from './PlanAirflowRun';
import PlanAxes from './PlanAxes';
import './plan.css';

// Margins around the plan, in plan units. The top one is deeper: it carries
// the column numbers *and* the hall banner, which would otherwise land on the
// first row's label.
const MARGIN = 16;
const MARGIN_TOP = 32;

export interface FloorPlanGraphicProps {
  layout: FloorPlanLayout;
  /** Fires with a unit id (CRAH/PDU/UPS) — seam for the asset detail panel. */
  onSelectUnit?: (_unitId: string) => void;
  /** Fires with a rack-row id — seam for row-level drill-in. */
  onSelectRow?: (_rowId: string) => void;
}

export default function FloorPlanGraphic({
  layout,
  onSelectUnit,
  onSelectRow,
}: FloorPlanGraphicProps) {
  const { w, h } = layout.viewBox;

  return (
    <svg
      className="plan-svg"
      viewBox={`${-MARGIN} ${-MARGIN_TOP} ${w + MARGIN * 2} ${h + MARGIN_TOP + MARGIN}`}
      role="img"
      aria-label={`Floor plan — ${layout.hallLabel}`}
      data-testid="floor-plan-graphic"
    >
      <g className="plan-shell">
        {layout.rooms.map(room => (
          <PlanRoomShape key={room.id} room={room} />
        ))}
      </g>

      <text x={-MARGIN} y={-MARGIN_TOP + 10} className="plan-hall-label">
        {layout.hallLabel}
      </text>

      <g className="plan-rows">
        {layout.rowBlocks.map(row => (
          <PlanRackRow key={row.id} row={row} onSelect={onSelectRow} />
        ))}
      </g>

      <g className="plan-flows">
        {layout.airflow.map(run => (
          <PlanAirflowRun key={run.id} run={run} />
        ))}
      </g>

      <g className="plan-units">
        {layout.units.map(unit => (
          <PlanUnitSymbol key={unit.id} unit={unit} onSelect={onSelectUnit} />
        ))}
      </g>

      <PlanAxes axes={layout.axes} />
    </svg>
  );
}
